import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Navigation, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { SOSDetailModal } from '@/components/SOS/SOSDetailModal';
import { AddPlaceModal } from './AddPlaceModal';
const defaultCenter: [number, number] = [42.4618, 14.2052]; // Pescara as fallback

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
  last_spotted_at: string | null;
  helper_count: number;
  created_at: string;
  updated_at: string;
}
interface Event {
  id: string;
  title: string;
  description?: string;
  event_date: string;
  latitude: number;
  longitude: number;
  location_name: string;
  status: string;
  cover_image_url?: string;
}
interface AdoptionPost {
  id: string;
  title: string;
  pet_name: string;
  species: string;
  breed?: string;
  latitude: number;
  longitude: number;
  location: string;
  image_url?: string;
  status: string;
}
interface UserLocation {
  id: string;
  user_id?: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
  location: string;
  latitude: number;
  longitude: number;
  last_updated?: string;
  accuracy?: number;
}
interface PlaceLocation {
  id: string;
  title: string;
  description?: string;
  address?: string;
  category: string;
  latitude: number;
  longitude: number;
  is_public: boolean;
  user_id: string;
  created_at: string;
}

// Removed sample locations - now using real data only

const getSOSTypeIcon = (type: string) => {
  switch (type) {
    case 'lost':
      return '🐕';
    case 'found':
      return '🔍';
    case 'emergency':
      return '🚨';
    case 'danger':
      return '⚠️';
    default:
      return '📍';
  }
};
const getSOSTypeColor = (type: string, status: string) => {
  if (status === 'resolved') return '#10b981'; // green
  if (status === 'cancelled') return '#6b7280'; // gray

  switch (type) {
    case 'lost':
      return '#ef4444';
    // red
    case 'found':
      return '#3b82f6';
    // blue  
    case 'emergency':
      return '#dc2626';
    // dark red
    case 'danger':
      return '#f59e0b';
    // orange
    default:
      return '#8b5cf6';
    // purple
  }
};
const createSOSIcon = (report: SOSReport) => {
  const color = getSOSTypeColor(report.type, report.status);
  const icon = getSOSTypeIcon(report.type);
  return L.divIcon({
    html: `
      <div style="
        background-color: ${color};
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        animation: ${report.status === 'open' ? 'pulse 2s infinite' : 'none'};
      ">
        ${icon}
      </div>
      <style>
        @keyframes pulse {
          0% { box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 0 ${color}40; }
          70% { box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 10px transparent; }
          100% { box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 0 transparent; }
        }
      </style>
    `,
    className: 'custom-sos-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
  });
};
interface LeafletBasicMapProps {
  onMarkerClick?: (type: string, item: any) => void;
  highlightedItems?: {
    type: string;
    items: any[];
  }[];
}
const LeafletBasicMap = ({
  onMarkerClick,
  highlightedItems
}: LeafletBasicMapProps) => {
  const { currentLocation, isSharing } = useLocationTracking();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const userPulseRef = useRef<L.Circle | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const [locating, setLocating] = useState(false);
  const [followingUser, setFollowingUser] = useState(false);
  const [sosReports, setSosReports] = useState<SOSReport[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [adoptions, setAdoptions] = useState<AdoptionPost[]>([]);
  const [users, setUsers] = useState<UserLocation[]>([]);
  const [places, setPlaces] = useState<PlaceLocation[]>([]);
  const [openSOSCount, setOpenSOSCount] = useState(0);
  const [selectedSOS, setSelectedSOS] = useState<SOSReport | null>(null);
  const [showSOSModal, setShowSOSModal] = useState(false);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [showAddPlaceModal, setShowAddPlaceModal] = useState(false);
  const [clickedCoords, setClickedCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    
    // Get user's current location first, then initialize map
    const initializeMap = async () => {
      let initialCenter = defaultCenter;
      let initialZoom = 12;
      
      try {
        const { getCurrentLocation } = await import('@/utils/geolocationUtils');
        const location = await getCurrentLocation({ 
          showToast: false,
          timeout: 5000 
        });
        
        if (location) {
          initialCenter = [location.lat, location.lng];
          initialZoom = 14;
          setUserLocation({ lat: location.lat, lng: location.lng });
          console.log('Map initialized with user location:', location);
        } else {
          console.log('Using default center (Pescara) - geolocation failed');
        }
      } catch (error) {
        console.log('Geolocation error, using default center:', error);
      }
      
      const map = L.map(containerRef.current!, {
        center: initialCenter,
        zoom: initialZoom,
        zoomControl: true
      });
      mapRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);

      // Create markers layer
      markersLayerRef.current = L.layerGroup().addTo(map);

      // Load all data
      loadSOSReports();
      loadEvents();
      loadAdoptions();
      loadUsers();
      loadPlaces();

      // Add click event to add places
      map.on('click', e => {
        setClickedCoords({
          lat: e.latlng.lat,
          lng: e.latlng.lng
        });
        setShowAddPlaceModal(true);
      });

      // Set up real-time subscription for user locations (guarded for environments without WebSocket)
      try {
        if (typeof window === 'undefined' || typeof (window as any).WebSocket === 'undefined') {
          console.warn('WebSocket not available, realtime updates disabled');
        } else {
          const channel = supabase
            .channel('realtime-user-locations')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'user_locations'
              },
              (payload) => {
                console.log('Real-time location update:', payload);
                loadUsers(); // Reload users when locations change
              }
            )
            .subscribe((status) => {
              if (status === 'CHANNEL_ERROR') {
                console.warn('WebSocket not available, realtime updates disabled');
              }
            });

          // Store channel reference for cleanup
          (mapRef.current as any)._realtimeChannel = channel;
        }
      } catch (error) {
        console.warn('WebSocket not available, realtime updates disabled:', error);
      }
    };
    
    initializeMap();
    return () => {
      // Cleanup real-time subscription
      if ((mapRef.current as any)?._realtimeChannel) {
        supabase.removeChannel((mapRef.current as any)._realtimeChannel);
      }
      if (mapRef.current) {
        mapRef.current.remove();
      }
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);
  const loadEvents = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('events').select('*').eq('status', 'active').gte('event_date', new Date().toISOString());
      if (error) throw error;
      const eventData = data as Event[] || [];
      setEvents(eventData);

      // Add events to map
      if (markersLayerRef.current && eventData.length > 0) {
        eventData.forEach(event => {
          const eventIcon = L.divIcon({
            html: `
              <div style="
                background: linear-gradient(135deg, #8B5CF6, #A855F7);
                color: white;
                border-radius: 50%;
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                font-weight: bold;
                border: 3px solid white;
                box-shadow: 0 2px 10px rgba(139, 92, 246, 0.4);
                position: relative;
              ">
                📅
              </div>
            `,
            className: 'custom-event-icon',
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          });
          const eventMarker = L.marker([event.latitude, event.longitude], {
            icon: eventIcon
          }).addTo(markersLayerRef.current!);
          eventMarker.bindPopup(`
            <div style="max-width: 200px;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${event.title}</h3>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
                📅 ${new Date(event.event_date).toLocaleDateString('it-IT', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
              </p>
              <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                📍 ${event.location_name}
              </p>
              ${event.description ? `<p style="margin: 0 0 8px 0; font-size: 12px;">${event.description.substring(0, 100)}...</p>` : ''}
              <button onclick="window.openEventDetail && window.openEventDetail('${event.id}')" style="
                background: linear-gradient(135deg, #8B5CF6, #A855F7);
                color: white;
                border: none;
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
              ">
                Vedi Evento
              </button>
            </div>
          `);
          eventMarker.on('click', () => {
            onMarkerClick?.('event', event);
          });
        });

        // Set up global function for opening event details
        (window as any).openEventDetail = (eventId: string) => {
          const event = eventData.find(e => e.id === eventId);
          if (event) {
            onMarkerClick?.('event', event);
          }
        };
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };
  const loadSOSReports = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('sos_reports').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setSosReports(data as SOSReport[] || []);
      const openCount = (data || []).filter(report => report.status === 'open').length;
      setOpenSOSCount(openCount);

      // Add SOS markers to map - only show open SOS reports
      if (markersLayerRef.current && data) {
        (data as SOSReport[]).filter((report: SOSReport) => report.status === 'open').forEach((report: SOSReport) => {
          const marker = L.marker([report.latitude, report.longitude], {
            icon: createSOSIcon(report)
          });
          const popupContent = `
            <div style="min-width: 200px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="font-size: 18px;">${getSOSTypeIcon(report.type)}</span>
                <strong style="font-size: 14px;">${report.title}</strong>
                <span style="background: ${getSOSTypeColor(report.type, report.status)}; color: white; padding: 2px 6px; border-radius: 12px; font-size: 10px;">${report.status.toUpperCase()}</span>
              </div>
              ${report.animal_species ? `<div style="margin-bottom: 4px; font-size: 12px; color: #666;">🐾 ${report.animal_species}${report.animal_breed ? ` (${report.animal_breed})` : ''}</div>` : ''}
              ${report.description ? `<div style="margin-bottom: 8px; font-size: 12px;">${report.description.substring(0, 100)}${report.description.length > 100 ? '...' : ''}</div>` : ''}
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #888;">
                <span>${report.helper_count} persone aiutano</span>
                <span>${new Date(report.created_at).toLocaleDateString('it-IT')}</span>
              </div>
              <div style="margin-top: 8px; text-align: center;">
                <button onclick="window.openSOSDetail('${report.id}')" style="background: #dc2626; color: white; padding: 6px 12px; border-radius: 6px; border: none; cursor: pointer; font-size: 12px; margin-right: 8px;">Visualizza dettagli</button>
                <a href="/app/sos" style="background: #3b82f6; color: white; padding: 6px 12px; border-radius: 6px; text-decoration: none; font-size: 12px;">Vai all'SOS</a>
              </div>
            </div>
          `;
          marker.bindPopup(popupContent);
          marker.addTo(markersLayerRef.current!);
        });

        // Set up global function for opening SOS details
        (window as any).openSOSDetail = (sosId: string) => {
          const report = (data as SOSReport[]).find(r => r.id === sosId);
          if (report) {
            setSelectedSOS(report);
            setShowSOSModal(true);
          }
        };
      }
    } catch (error) {
      console.error('Error loading SOS reports:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i report SOS",
        variant: "destructive"
      });
    }
  };
  const loadAdoptions = async () => {
    try {
      // First get adoption posts with location
      const { data: adoptionData, error: adoptionError } = await supabase
        .from('adoption_posts')
        .select('*')
        .eq('status', 'published')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (adoptionError) throw adoptionError;

      // Then get profiles for each user
      const userIds = adoptionData?.map(post => post.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const data = adoptionData?.map(post => ({
        ...post,
        profiles: profilesData?.find(profile => profile.user_id === post.user_id)
      })) || [];
      
      const adoptionsWithProfiles = data as AdoptionPost[] || [];
      setAdoptions(adoptionsWithProfiles);

      // Add adoption markers to map
      if (markersLayerRef.current && adoptionsWithProfiles.length > 0) {
        adoptionsWithProfiles.forEach(adoption => {
          const adoptionIcon = L.divIcon({
            html: `
              <div style="
                background: linear-gradient(135deg, #ef4444, #dc2626);
                color: white;
                border-radius: 50%;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                font-weight: bold;
                border: 3px solid white;
                box-shadow: 0 2px 10px rgba(239, 68, 68, 0.4);
              ">
                ❤️
              </div>
            `,
            className: 'custom-adoption-icon',
            iconSize: [36, 36],
            iconAnchor: [18, 18]
          });
          const marker = L.marker([adoption.latitude, adoption.longitude], {
            icon: adoptionIcon
          }).addTo(markersLayerRef.current!);
          marker.bindPopup(`
            <div style="max-width: 200px;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${adoption.pet_name}</h3>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
                🐾 ${adoption.species}${adoption.breed ? ` (${adoption.breed})` : ''}
              </p>
              <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                📍 ${adoption.location}
              </p>
              <button onclick="window.openAdoptionDetail && window.openAdoptionDetail('${adoption.id}')" style="
                background: linear-gradient(135deg, #ef4444, #dc2626);
                color: white;
                border: none;
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
              ">
                Vedi Adozione
              </button>
            </div>
          `);
          marker.on('click', () => {
            onMarkerClick?.('adoption', adoption);
          });
        });

        // Set up global function for opening adoption details
        (window as any).openAdoptionDetail = (adoptionId: string) => {
          onMarkerClick?.('adoption', data.find((a: any) => a.id === adoptionId));
        };
      }
    } catch (error) {
      console.error('Error loading adoptions:', error);
    }
  };
  const loadUsers = async () => {
    try {
      // Semplifica la query senza i join complessi per evitare errori di tipo
      const {
        data: userLocations,
        error
      } = await (supabase as any).from('user_locations').select('*').eq('is_sharing_enabled', true).gte('last_updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()); // Last 24 hours

      if (error) {
        console.error('Error loading user locations:', error);
        return;
      }
      if (!userLocations || userLocations.length === 0) {
        console.log('No user locations found');
        setUsers([]);
        return;
      }

      // Ottieni i profili separatamente
      const userIds = userLocations.map((location: any) => location.user_id);
      const {
        data: profiles,
        error: profilesError
      } = await supabase.from('profiles').select('user_id, username, display_name, avatar_url').in('user_id', userIds);
      if (profilesError) {
        console.error('Error loading profiles:', profilesError);
      }

      // Combina i dati
      const usersWithCoords = userLocations.map((location: any) => {
        const profile = profiles?.find((p: any) => p.user_id === location.user_id);
        return {
          id: location.id,
          user_id: location.user_id,
          username: profile?.username || 'Utente sconosciuto',
          display_name: profile?.display_name,
          avatar_url: profile?.avatar_url,
          latitude: location.latitude,
          longitude: location.longitude,
          location: `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`,
          last_updated: location.last_updated_at,
          accuracy: location.accuracy
        };
      }) as UserLocation[];
      setUsers(usersWithCoords);
      console.log('Users with real location loaded:', usersWithCoords.length);

      // Add user markers to map  
      if (markersLayerRef.current && usersWithCoords.length > 0) {
        usersWithCoords.forEach(user => {
          if (!user.latitude || !user.longitude) return;

          // Check if location is recent (within last hour for active status)
          const isRecent = new Date(user.last_updated!).getTime() > Date.now() - 60 * 60 * 1000;
          const userIcon = L.divIcon({
            html: `
              <div style="
                background: linear-gradient(135deg, ${isRecent ? '#10b981' : '#3b82f6'}, ${isRecent ? '#059669' : '#1d4ed8'});
                color: white;
                border-radius: 50%;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: bold;
                border: 2px solid white;
                box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
                position: relative;
              ">
                👤
                ${isRecent ? '<div style="position: absolute; top: -2px; right: -2px; width: 8px; height: 8px; background: #10b981; border-radius: 50%; border: 1px solid white;"></div>' : ''}
              </div>
            `,
            className: 'custom-user-icon',
            iconSize: [32, 32],
            iconAnchor: [16, 16]
          });
          const marker = L.marker([user.latitude, user.longitude], {
            icon: userIcon
          }).addTo(markersLayerRef.current!);
          const lastUpdate = new Date(user.last_updated!).toLocaleString('it-IT');
          marker.bindPopup(`
            <div style="max-width: 180px;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${user.display_name || user.username}</h3>
              <div style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                <p style="margin: 0;">📍 ${user.location}</p>
                <p style="margin: 4px 0 0 0; font-size: 11px; color: ${isRecent ? '#10b981' : '#888'};">
                  ${isRecent ? '🟢 Online ora' : `⏰ Visto ${lastUpdate}`}
                </p>
              </div>
              <button onclick="window.openUserProfile && window.openUserProfile('${user.username}')" style="
                background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                color: white;
                border: none;
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                width: 100%;
              ">
                Vedi Profilo
              </button>
            </div>
          `);
          marker.on('click', () => {
            onMarkerClick?.('user', user);
          });
        });

        // Set up global function for opening user profiles
        (window as any).openUserProfile = (username: string) => {
          const user = usersWithCoords.find(u => u.username === username);
          if (user) {
            onMarkerClick?.('user', user);
          }
        };
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };
  const loadPlaces = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('locations').select('*').eq('is_public', true).order('created_at', {
        ascending: false
      });
      if (error) {
        console.error('Error loading places:', error);
        return;
      }
      const placeData = data as PlaceLocation[] || [];
      setPlaces(placeData);

      // Add place markers to map
      if (markersLayerRef.current && placeData.length > 0) {
        placeData.forEach(place => {
          const categoryConfig = getCategoryConfig(place.category);
          const placeIcon = L.divIcon({
            html: `
              <div style="
                background: linear-gradient(135deg, ${categoryConfig.color}, ${categoryConfig.darkColor});
                color: white;
                border-radius: 50%;
                width: 34px;
                height: 34px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: bold;
                border: 2px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              ">
                ${categoryConfig.emoji}
              </div>
            `,
            className: 'custom-place-icon',
            iconSize: [34, 34],
            iconAnchor: [17, 17]
          });
          const marker = L.marker([place.latitude, place.longitude], {
            icon: placeIcon
          }).addTo(markersLayerRef.current!);
          marker.bindPopup(`
            <div style="max-width: 200px;">
              <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${place.title}</h3>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
                ${categoryConfig.emoji} ${categoryConfig.label}
              </p>
              ${place.address ? `<p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">📍 ${place.address}</p>` : ''}
              ${place.description ? `<p style="margin: 0 0 8px 0; font-size: 12px;">${place.description.substring(0, 100)}${place.description.length > 100 ? '...' : ''}</p>` : ''}
              <div style="font-size: 11px; color: #888; text-align: center; margin-top: 8px;">
                Aggiunto il ${new Date(place.created_at).toLocaleDateString('it-IT')}
              </div>
            </div>
          `);
        });
      }
    } catch (error) {
      console.error('Error loading places:', error);
    }
  };
  const getCategoryConfig = (category: string) => {
    const configs: Record<string, {
      emoji: string;
      label: string;
      color: string;
      darkColor: string;
    }> = {
      'veterinari': {
        emoji: '🏥',
        label: 'Veterinari e cliniche',
        color: '#ef4444',
        darkColor: '#dc2626'
      },
      'parchi': {
        emoji: '🌳',
        label: 'Parchi e aree verdi',
        color: '#22c55e',
        darkColor: '#16a34a'
      },
      'negozi': {
        emoji: '🛍️',
        label: 'Negozi per animali',
        color: '#8b5cf6',
        darkColor: '#7c3aed'
      },
      'adozioni': {
        emoji: '❤️',
        label: 'Centri adozione',
        color: '#f59e0b',
        darkColor: '#d97706'
      },
      'colonie-feline': {
        emoji: '🐱',
        label: 'Colonie feline',
        color: '#06b6d4',
        darkColor: '#0891b2'
      },
      'canili': {
        emoji: '🏠',
        label: 'Canili',
        color: '#6366f1',
        darkColor: '#4f46e5'
      }
    };
    return configs[category] || {
      emoji: '📍',
      label: 'Altro',
      color: '#6b7280',
      darkColor: '#4b5563'
    };
  };
  // Update user marker when current location changes from location tracking
  useEffect(() => {
    if (!mapRef.current || !currentLocation) return;
    
    const latlng: [number, number] = [currentLocation.lat, currentLocation.lng];
    const map = mapRef.current;
    
    setUserLocation({
      lat: currentLocation.lat,
      lng: currentLocation.lng
    });

    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng(latlng);
    } else {
      userMarkerRef.current = L.marker(latlng, {
        icon: L.divIcon({
          html: `<div style="
            background: ${isSharing ? '#22c55e' : '#3b82f6'};
            width: 20px;
            height: 20px;
            border-radius: 50%;
            border: 3px solid #fff;
            box-shadow: 0 0 0 3px rgba(${isSharing ? '34,197,94' : '59,130,246'},.3);
            animation: ${isSharing ? 'pulse 2s infinite' : 'none'};
          "></div>
          <style>
            @keyframes pulse {
              0% { box-shadow: 0 0 0 3px rgba(${isSharing ? '34,197,94' : '59,130,246'},.3); }
              70% { box-shadow: 0 0 0 10px transparent; }
              100% { box-shadow: 0 0 0 3px transparent; }
            }
          </style>`,
          className: 'user-location-icon',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        })
      }).addTo(map).bindPopup(`<strong>📍 La tua posizione${isSharing ? ' (condivisa)' : ''}</strong>`);
    }

    if (userPulseRef.current) {
      userPulseRef.current.setLatLng(latlng);
    } else {
      userPulseRef.current = L.circle(latlng, {
        radius: 60,
        color: isSharing ? '#22c55e' : '#3b82f6',
        fillColor: isSharing ? '#22c55e' : '#3b82f6',
        fillOpacity: 0.15
      }).addTo(map);
    }

    // Only fly to location if we're following the user
    if (followingUser) {
      map.flyTo(latlng, 16, {
        duration: 0.75
      });
    }
  }, [currentLocation, isSharing, followingUser]);

  const locate = async () => {
    if (!mapRef.current) return;
    
    // If we already have location from tracking, just center on it
    if (currentLocation) {
      const latlng: [number, number] = [currentLocation.lat, currentLocation.lng];
      setFollowingUser(true);
      mapRef.current.flyTo(latlng, 16, { duration: 0.75 });
      
      // Auto-disable following after 30 seconds
      setTimeout(() => setFollowingUser(false), 30000);
      return;
    }
    
    if (locating) return;
    setLocating(true);

    try {
      const { getCurrentLocationWithRetry } = await import('@/utils/geolocationUtils');
      const location = await getCurrentLocationWithRetry(2);
      if (!location) {
        alert('Impossibile ottenere la tua posizione. Controlla i permessi di geolocalizzazione.');
        setLocating(false);
        return;
      }

      const { lat, lng } = location;
      const latlng: [number, number] = [lat, lng];
      const map = mapRef.current!;

      setUserLocation({ lat, lng });

      if (userMarkerRef.current) {
        userMarkerRef.current.setLatLng(latlng);
      } else {
        userMarkerRef.current = L.marker(latlng, {
          icon: L.divIcon({
            html: '<div style="background:#3b82f6;width:20px;height:20px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 3px rgba(59,130,246,.3)"></div>',
            className: 'user-location-icon',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        }).addTo(map).bindPopup('<strong>📍 La tua posizione</strong>');
      }

      if (userPulseRef.current) {
        userPulseRef.current.setLatLng(latlng);
      } else {
        userPulseRef.current = L.circle(latlng, {
          radius: Math.max(location.accuracy || 60, 30),
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.15
        }).addTo(map);
      }

      map.flyTo(latlng, 16, { duration: 0.75 });
      setFollowingUser(true);
      
      // Auto-disable following after 30 seconds
      setTimeout(() => setFollowingUser(false), 30000);
    } catch (error) {
      console.error('Enhanced geolocation failed:', error);
      alert('Errore geolocalizzazione. Verifica i permessi di localizzazione del browser/app.');
    } finally {
      setLocating(false);
    }
  };
  return <div className="relative w-full h-[calc(100vh-120px)] rounded-lg overflow-hidden">
      <div ref={containerRef} className="absolute inset-0" />
      
      {/* Badges */}
      <div className="absolute top-4 left-4 z-[1000] space-y-2">
        {openSOSCount > 0 && <Badge variant="destructive" className="flex items-center gap-2 shadow-lg">
            <AlertTriangle className="w-3 h-3" />
            {openSOSCount} SOS attivi
          </Badge>}
        {events.length > 0 && <Badge variant="secondary" className="bg-purple-100 text-purple-800 shadow-lg">
            📅 {events.length} Eventi
          </Badge>}
        {adoptions.length > 0 && <Badge variant="secondary" className="bg-red-100 text-red-800 shadow-lg">
            ❤️ {adoptions.length} Adozioni
          </Badge>}
        {users.length > 0 && <Badge variant="secondary" className="bg-blue-100 text-blue-800 shadow-lg">
            👤 {users.length} Utenti
          </Badge>}
        {places.length > 0 && <Badge variant="secondary" className="bg-green-100 text-green-800 shadow-lg">
            📍 {places.length} Luoghi
          </Badge>}
      </div>
      
      {/* Location Button */}
      <Button 
        onClick={locate} 
        size="sm" 
        className={`absolute top-4 right-4 z-[1000] shadow-lg ${followingUser ? 'bg-green-600 hover:bg-green-700' : ''}`} 
        disabled={locating}
      >
        <Navigation className={`w-4 h-4 mr-2 ${followingUser ? 'animate-pulse' : ''}`} />
        {locating ? 'Locazione...' : followingUser ? 'Seguendo...' : 'La mia posizione'}
      </Button>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 backdrop-blur-sm p-3 shadow-lg border z-[1000] rounded-lg bg-zinc-500">
        <h4 className="font-semibold text-sm mb-2">Legenda</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center">
            <span className="mr-2">🚨</span>
            <span>SOS Emergenze</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">📅</span>
            <span>Eventi</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">❤️</span>
            <span>Adozioni</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">👤</span>
            <span>Utenti nelle vicinanze</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">📍</span>
            <span>Luoghi segnalati</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">🎯</span>
            <span>La tua posizione</span>
          </div>
          <div className="text-xs text-muted-foreground mt-2 bg-transparent">
            Clicca sulla mappa per aggiungere un luogo
          </div>
        </div>
      </div>

      {/* SOS Detail Modal */}
      <SOSDetailModal sos={selectedSOS} open={showSOSModal} onOpenChange={setShowSOSModal} onUpdate={() => {
      loadSOSReports();
      setShowSOSModal(false);
    }} />

      {/* Add Place Modal */}
      {clickedCoords && <AddPlaceModal isOpen={showAddPlaceModal} onClose={() => {
      setShowAddPlaceModal(false);
      setClickedCoords(null);
    }} latitude={clickedCoords.lat} longitude={clickedCoords.lng} onPlaceAdded={() => {
      loadPlaces();
      setShowAddPlaceModal(false);
      setClickedCoords(null);
    }} />}
    </div>;
};
export default LeafletBasicMap;